import React, { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "../common/Reveal";
import { InfoModal } from "../modals/Modals";
import { CERT_MODAL } from "../../data/certifications";
import { certificationService, resolveImageUrl } from "../../services/catalogService";
import { useLanguage } from "../../context/LanguageContext";

// Task 14 — certifications come from MongoDB; the admin uploads the official
// logos. Until a logo is uploaded, the text name shows as placeholder.
export default function Certifications() {
  const { t, localize } = useLanguage();
  const [open, setOpen] = useState(false);
  const [certs, setCerts] = useState([]);

  useEffect(() => {
    certificationService.list().then(setCerts).catch(() => setCerts([]));
  }, []);

  return (
    <section className="bg-white py-24 md:py-32" data-testid="certifications-section">
      <div className="container-tessa grid grid-cols-1 items-center gap-14 lg:grid-cols-2 lg:gap-24">
        <Reveal>
          <div className="mb-8 h-px w-16 bg-[#C4A46B]" />
          <h3 className="font-serif text-3xl leading-tight text-[#183C32] md:text-4xl">
            {t("certifications.leftTitle")}
          </h3>
          <p className="mt-6 max-w-md leading-relaxed text-[#817B74]">
            {t("certifications.leftText")}
          </p>
        </Reveal>

        <Reveal>
          <span className="font-sans text-xs uppercase tracking-[0.24em] text-[#D92A47]">
            {t("certifications.eyebrow")}
          </span>
          <h2 className="mt-4 font-serif text-4xl leading-[1.05] text-[#252525] md:text-5xl">
            {t("certifications.title1")}
            <br />
            <span className="italic text-[#D92A47]">{t("certifications.title2")}</span>
          </h2>

          <div className="mt-10 grid grid-cols-2 gap-px border border-black/10 bg-black/10" data-testid="certifications-grid">
            {certs.map((certification) => (
              <div
                key={certification.id}
                data-testid={`cert-${certification.id}`}
                className="flex min-h-[120px] flex-col items-center justify-center bg-white p-8 text-center opacity-60 grayscale transition-all duration-500 hover:opacity-100 hover:grayscale-0"
              >
                {certification.logo ? (
                  <img
                    src={resolveImageUrl(certification.logo)}
                    alt={certification.name}
                    className="max-h-14 max-w-full object-contain"
                  />
                ) : (
                  <span className="font-serif text-2xl tracking-wide text-[#183C32] uppercase">
                    {certification.name}
                  </span>
                )}
                {certification.subtitle && (
                  <span className="mt-1 font-sans text-[10px] uppercase tracking-[0.24em] text-[#817B74]">
                    {certification.subtitle}
                  </span>
                )}
              </div>
            ))}
          </div>

          <button
            onClick={() => setOpen(true)}
            data-testid="cert-learn-more"
            className="group mt-8 inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.16em] text-[#D92A47]"
          >
            {t("certifications.learnMore")}
            <ArrowUpRight
              size={16}
              className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
            />
          </button>
        </Reveal>
      </div>

      <InfoModal
        open={open}
        onClose={() => setOpen(false)}
        title={localize(CERT_MODAL, "title")}
      >
        <p>{localize(CERT_MODAL, "body")}</p>
        <div className="mt-6 flex flex-wrap gap-3">
          {certs.map((certification) => (
            <span
              key={certification.id}
              className="border border-black/10 px-3 py-1.5 text-xs uppercase tracking-[0.14em] text-[#183C32]"
            >
              {certification.name}
            </span>
          ))}
        </div>
      </InfoModal>
    </section>
  );
}
